/**
 * Maps AddressSelector ISO country + region codes to English names stored on Company
 * (aligned with settings / timezoneMapping keys).
 */

export const ISO2_TO_COUNTRY_ENGLISH_NAME = {
  US: 'United States',
  CA: 'Canada',
  GB: 'United Kingdom',
  AU: 'Australia',
  DE: 'Germany',
  FR: 'France',
  IN: 'India',
  JP: 'Japan',
  CN: 'China',
  BR: 'Brazil',
  MX: 'Mexico',
  IT: 'Italy',
  ES: 'Spain',
  NL: 'Netherlands',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  CH: 'Switzerland',
  AT: 'Austria',
  BE: 'Belgium',
  PT: 'Portugal',
  GR: 'Greece',
  PL: 'Poland',
  CZ: 'Czech Republic',
  HU: 'Hungary',
  RO: 'Romania',
  BG: 'Bulgaria',
  HR: 'Croatia',
  IE: 'Ireland',
  IS: 'Iceland',
  LU: 'Luxembourg',
  MT: 'Malta',
  SK: 'Slovakia',
  SI: 'Slovenia',
  EE: 'Estonia',
  LV: 'Latvia',
  LT: 'Lithuania',
  RU: 'Russia',
  UA: 'Ukraine',
  TR: 'Turkey',
  SA: 'Saudi Arabia',
  AE: 'United Arab Emirates',
  IL: 'Israel',
  ZA: 'South Africa',
  EG: 'Egypt',
  NG: 'Nigeria',
  KE: 'Kenya',
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  PE: 'Peru',
  VE: 'Venezuela',
  EC: 'Ecuador',
  UY: 'Uruguay',
  PY: 'Paraguay',
  BO: 'Bolivia',
  KR: 'South Korea',
  TH: 'Thailand',
  VN: 'Vietnam',
  MY: 'Malaysia',
  SG: 'Singapore',
  PH: 'Philippines',
  ID: 'Indonesia',
  PK: 'Pakistan',
  BD: 'Bangladesh',
  LK: 'Sri Lanka',
  NZ: 'New Zealand',
};

const US_STATE_CODE_TO_NAME = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas',
  KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts',
  MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
  NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

const GB_REGION_TO_NAME = {
  ENG: 'England',
  SCT: 'Scotland',
  WLS: 'Wales',
  NIR: 'Northern Ireland',
};

const CA_PROVINCE_CODE_TO_NAME = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', NT: 'Northwest Territories',
  NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec', SK: 'Saskatchewan', YT: 'Yukon',
};

const AU_STATE_CODE_TO_NAME = {
  NSW: 'New South Wales', VIC: 'Victoria', QLD: 'Queensland', WA: 'Western Australia',
  SA: 'South Australia', TAS: 'Tasmania', NT: 'Northern Territory', ACT: 'Australian Capital Territory',
};

export function countryIsoCodeToEnglishName(iso2) {
  if (!iso2 || typeof iso2 !== 'string') return '';
  const c = iso2.trim().toUpperCase();
  return ISO2_TO_COUNTRY_ENGLISH_NAME[c] || iso2;
}

export function regionCodeToEnglishName(countryIso2, regionCode) {
  if (!regionCode) return '';
  const cc = (countryIso2 || '').trim().toUpperCase();
  const r = String(regionCode).trim();
  if (cc === 'US') return US_STATE_CODE_TO_NAME[r] || r;
  if (cc === 'GB') return GB_REGION_TO_NAME[r] || r;
  if (cc === 'CA') return CA_PROVINCE_CODE_TO_NAME[r] || r;
  if (cc === 'AU') return AU_STATE_CODE_TO_NAME[r] || r;
  return r;
}

/** Maps IANA zone from getTimezoneByLocation to CreateCompany timezone dropdown labels */
export const IANA_TO_COMPANY_TIMEZONE_LABEL = {
  'America/New_York': 'UTC-05:00 Eastern Time (US & Canada), Bogota, Lima',
  'America/Chicago': 'UTC-06:00 Central Time (US & Canada), Mexico City',
  'America/Denver': 'UTC-07:00 Mountain Time (US & Canada)',
  'America/Los_Angeles': 'UTC-08:00 Pacific Time (US & Canada)',
  'America/Phoenix': 'UTC-07:00 Mountain Time (US & Canada)',
  'America/Anchorage': 'UTC-09:00 Alaska, French Polynesia',
  'Pacific/Honolulu': 'UTC-10:00 Hawaii, Cook Islands',
  'America/Toronto': 'UTC-05:00 Eastern Time (US & Canada), Bogota, Lima',
  'America/Montreal': 'UTC-05:00 Eastern Time (US & Canada), Bogota, Lima',
  'America/Vancouver': 'UTC-08:00 Pacific Time (US & Canada)',
  'America/Edmonton': 'UTC-07:00 Mountain Time (US & Canada)',
  'America/Winnipeg': 'UTC-06:00 Central Time (US & Canada), Mexico City',
  'America/Regina': 'UTC-06:00 Central Time (US & Canada), Mexico City',
  'America/Halifax': 'UTC-04:00 Atlantic Time (Canada), Caracas, La Paz',
  'America/St_Johns': 'UTC-03:30 Newfoundland',
  'America/Yellowknife': 'UTC-07:00 Mountain Time (US & Canada)',
  'America/Iqaluit': 'UTC-05:00 Eastern Time (US & Canada), Bogota, Lima',
  'America/Whitehorse': 'UTC-08:00 Pacific Time (US & Canada)',
  'Europe/London': 'UTC+00:00 London, Dublin, Lisbon',
  'Asia/Kolkata': 'UTC+05:30 Kolkata, India',
  'Australia/Sydney': 'UTC+10:00 Sydney, Melbourne, Brisbane',
  'Australia/Melbourne': 'UTC+10:00 Sydney, Melbourne, Brisbane',
  'Australia/Brisbane': 'UTC+10:00 Sydney, Melbourne, Brisbane',
  'Australia/Perth': 'UTC+08:00 Beijing, Hong Kong, Singapore',
  'Australia/Adelaide': 'UTC+09:30 Adelaide, Darwin',
  'Australia/Darwin': 'UTC+09:30 Adelaide, Darwin',
  'Australia/Hobart': 'UTC+10:00 Sydney, Melbourne, Brisbane',
  'Asia/Tokyo': 'UTC+09:00 Tokyo, Seoul, Osaka',
  'Asia/Shanghai': 'UTC+08:00 Beijing, Hong Kong, Singapore',
  'Europe/Berlin': 'UTC+01:00 Berlin, Paris, Rome, Madrid',
  'Europe/Paris': 'UTC+01:00 Berlin, Paris, Rome, Madrid',
  'America/Mexico_City': 'UTC-06:00 Central Time (US & Canada), Mexico City',
};
